import React from 'react';
import { PropTypes } from 'prop-types';
import { updateStatementRow, deleteStatementRow } from 'actions';
import FormTypes from 'common/Forms/FormTypes';
import StatementTypes from 'common/Forms/StatementTypes';
import LOBCodes from 'common/Customers/LimitationOnBenefits';
import ValidationInspector from 'common/Validation/ValidationInspector';
import valueOrEmpty from 'common/App/valueOrEmpty';
import AccessControl from 'components/AccessControl';
import ActivityPerms from 'components/AccessControl/permissions.json';
import { FormViewButton } from 'components/Common/Buttons/FormViewButton';
import Chapter3Status from 'common/Customers/Chapter3Status';
import Chapter4Status from 'common/Customers/Chapter4Status';
import WhdValidationSummary from 'components/WhdStatement/WhdValidationSummary';
import JurisdictionDropDown from 'components/Common/Inputs/JurisdictionDropDown';
import ConfirmModal from 'components/Modals/ConfirmModal';
import removeInvalidHtmlChars from 'common/App/removeInvalidHtmlChars';
import StatementAllocationBlock from './statementAllocationBlock';
import TierAllocationBlock from './tierAllocationBlock';
import TierOwnershipBlock from './tierOwnershipBlock';

class BeneficialOwner extends React.PureComponent {
  static propTypes = {
    beneficialOwner: PropTypes.shape({
      basicDetails: PropTypes.shape({ name: PropTypes.string, formEntityName: PropTypes.string, formType: PropTypes.number }),
      recipients: PropTypes.arrayOf(PropTypes.shape({ allocationIncomeRate: PropTypes.number, validationLineNo: PropTypes.number })),
      guid: PropTypes.string,
      ownerDetails: PropTypes.shape({
        name: PropTypes.string,
        guid: PropTypes.string,
        accountNumber: PropTypes.string,
        disregardedEntityName: PropTypes.string,
        foreignTIN: PropTypes.string,
        giin: PropTypes.string,
        tin: PropTypes.string,
        active: PropTypes.string,
        address: PropTypes.string,
        allocationIncomeRate: PropTypes.number,
        chapter3RecipientCode: PropTypes.string,
        city: PropTypes.string,
        country: PropTypes.string,
        decimalPrecision: PropTypes.number,
        fatcaStatus: PropTypes.string,
        formType: PropTypes.number,
        limitationOnBenefits: PropTypes.number,
        new: PropTypes.string,
        stateProvince: PropTypes.string,
        taxResidenceCountry: PropTypes.string,
        tinType: PropTypes.string,
        treatyBenefits: PropTypes.string,
        usNonUsStatus: PropTypes.string,
        zip: PropTypes.string,
        isTier: PropTypes.bool,
      }),
      statementIndex: PropTypes.number
    }).isRequired,
    decimalPrecision: PropTypes.number,
    getChapter3DescriptionByName: PropTypes.func.isRequired,
    getChapter4DescriptionByName: PropTypes.func.isRequired,
    isParentTierExpanded: PropTypes.bool,
    isTopTierExpanded: PropTypes.bool,
    openChangeParentModal: PropTypes.func.isRequired,
    parents: PropTypes.arrayOf(PropTypes.string),
    statementType: PropTypes.number,
    idx: PropTypes.number.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    dispatch: PropTypes.func.isRequired,
    openAccountWithholdingModal: PropTypes.func.isRequired,
    openAddFormModal: PropTypes.func.isRequired,
    topAccountId: PropTypes.number.isRequired,
    addBeneficialOwner: PropTypes.func.isRequired,
    isSavingStatement: PropTypes.bool,
    isJointAccount: PropTypes.bool,
    validationLineNo: PropTypes.number,
    validationResult: PropTypes.shape({
      valid: PropTypes.bool,
      rejections: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })),
      warnings: PropTypes.arrayOf(PropTypes.shape({ code: PropTypes.string })),
      lines: PropTypes.arrayOf(PropTypes.shape({}))
    }),
    openPooledRatesModal: PropTypes.func.isRequired,
    updateToggledCollapsibleTiers: PropTypes.func.isRequired,
    tierCollapseStructure: PropTypes.shape({}),
    // eslint-disable-next-line react/no-unused-prop-types
    parentAccountId: PropTypes.number,
    isDeletingRow: PropTypes.bool,
    isStructureValid: PropTypes.bool,
    isAddingNewBeneficalOwner: PropTypes.bool,
    statementVersion: PropTypes.string,
    hideTierAllocationValue: PropTypes.bool,
    selectedPills: PropTypes.shape({
      missingForms: PropTypes.bool,
      invalidForms: PropTypes.bool,
      inReviewForms: PropTypes.bool,
      rejections: PropTypes.bool,
      warnings: PropTypes.bool,
      missingAllocation: PropTypes.bool,
    })
  };

  static defaultProps = {
    parents: null,
    decimalPrecision: 2,
    isParentTierExpanded: false,
    isTopTierExpanded: false,
    statementType: 9,
    permissions: null,
    isSavingStatement: false,
    isJointAccount: false,
    validationResult: null,
    validationLineNo: null,
    tierCollapseStructure: null,
    isDeletingRow: false,
    isStructureValid: false,
    parentAccountId: null,
    isAddingNewBeneficalOwner: false,
    statementVersion: null,
    hideTierAllocationValue: false,
    selectedPills: null
  };

  static buildElementId(name) {
    return (name && name.replace(/[^a-zA-Z0-9]/g, '')) || '';
  }

  static cleanString(value) {
    return (value && JSON.stringify(value).replace(/[^\p{L}\d]/gu, '')) ?? null;
  }

  static isAllocationFromChildren(beneficialOwner) {
    const allocation = (beneficialOwner.ownerDetails && beneficialOwner.ownerDetails.allocationIncomeRate) || beneficialOwner.allocationIncomeRate || 0;

    if (allocation === 0 && beneficialOwner.recipients) {
      return true;
    }

    return false;
  }

  static shouldDisplayTierAllocationValue(level, isImy) {
    return level && (isImy ? level > 2 : level > 1);
  }

  static shouldDisplayTierOwnership(beneficialOwner) {
    return (beneficialOwner.recipients);
  }

  static roundPrecision = (val, precision) => val.toFixed(precision);

  static parsePrecision = (str, precision) => {
    let newstr = str.toString();
    newstr = newstr.slice(0, (newstr.indexOf('.')) + precision + 1);
    return Number(newstr);
  };

  static toPrecision(value, decimalPrecision = 0) {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return null;
    } if (value === 100) {
      return '100';
    }
    return this.parsePrecision(value, decimalPrecision);
  }

  static isBoFormMissing = recipient => recipient?.basicDetails && !recipient.basicDetails.formId;

  static isBoFormValid = recipient => !BeneficialOwner.isBoFormMissing(recipient) && recipient?.basicDetails?.formIsValid;

  static isBoFormInvalid = (recipient, isPooled) => !BeneficialOwner.isBoFormMissing(recipient) && !recipient?.basicDetails?.formIsValid && !isPooled;

  static isFormInReviewStage = (recipient) => {
    const workflowStage = recipient.basicDetails?.workflowStage;
    return workflowStage >= 4 && workflowStage <= 9;
  };

  static isOwnerMissingAllocation = (recipient, isPooled) => {
    let allocation = null;
    if (isPooled) {
      allocation = recipient?.allocationIncomeRate;
    } else {
      allocation = recipient.ownerDetails?.allocationIncomeRate;
    }
    return !allocation && !Array.isArray(recipient.recipients);
  };

  static getOwnerValidationState = (recipient, validationResult) => {
    if (!validationResult?.lines || validationResult.lines.length === 0) {
      return null;
    }

    const lineNo = recipient && (recipient.lineNo || (recipient.ownerDetails && recipient.ownerDetails.lineNo > 0 && recipient.ownerDetails.lineNo));
    const lineValidation = lineNo > 0 && validationResult.lines.find(result => result.lineNumber === lineNo);

    return lineNo > 0 ? {
      rejectionCount: ValidationInspector.filterRejections(lineValidation)?.length || 0,
      warningCount: ValidationInspector.filterWarnings(lineValidation)?.length || 0
    } : null;
  };

  static checkRecipientFilterMatches(recipient, validationResults, isPooled) {
    if (recipient.filterMatches) {
      return recipient.filterMatches;
    }

    const isFormMissing = BeneficialOwner.isBoFormMissing(recipient);
    const isFormValid = BeneficialOwner.isBoFormValid(recipient);
    const isFormInvalid = BeneficialOwner.isBoFormInvalid(recipient, isPooled);
    const isFormInReviewStage = BeneficialOwner.isFormInReviewStage(recipient);
    const isBoMissingAllocation = BeneficialOwner.isOwnerMissingAllocation(recipient, isPooled);

    const boValidationSummary = validationResults && BeneficialOwner.getOwnerValidationState(recipient, validationResults);

    const warningCount = boValidationSummary?.warningCount || null;
    const rejectionCount = boValidationSummary?.rejectionCount || null;

    recipient.filterMatches = {
      isFormMissing,
      isFormValid,
      isFormInvalid,
      isFormInReviewStage,
      isBoMissingAllocation,
      warningCount,
      rejectionCount
    };

    return recipient.filterMatches;
  }

  static getTotalStatementAllocation = (structure, decimalPrecision = 8) => {
    const totalValue = ((structure && structure.recipients) || []).reduce((p, c) => (p || 0) + BeneficialOwner.parsePrecision(BeneficialOwner.roundPrecision(c.statementAllocation * 100, decimalPrecision), decimalPrecision), 0);
    return BeneficialOwner.parsePrecision(totalValue, decimalPrecision);
  };

  static getRecipientAllocationErrors(recipients) {
    let result = [];

    recipients.forEach((element) => {
      const chapter3Status = element.ownerDetails?.chapter3RecipientCode || '';
      const directAllocationAllowed = chapter3Status && Chapter3Status.findByName(chapter3Status).directAllocationAllowed;
      if (!directAllocationAllowed && element.recipients) {
        const ownership = BeneficialOwner.getTierOwnershipPercent(element, 2);
        if (ownership !== 100) {
          result = [...result, {
            id: `ownership-${ element?.ownerDetails?.name }`,
            errorOwner: element?.ownerDetails?.name,
            errorType: 'Ownership',
            errorValue: ownership
          }];
        }
        if (element.statementAllocation === 0) {
          result = [...result, {
            id: `whs-allocation-${ element?.ownerDetails?.name }`,
            errorOwner: element?.ownerDetails?.name,
            errorType: 'WHS Allocation',
            errorValue: 0
          }];
        }
        const childResults = BeneficialOwner.getRecipientAllocationErrors(element.recipients);
        result = [...result, ...childResults];
      }
    });

    return result;
  }

  static getAllocationErrors(structure, decimalPrecision) {
    let recipientErrors = [];
    if (structure) {
      const ownership = this.getTotalStatementAllocation(structure, decimalPrecision);
      if (ownership !== 100) {
        recipientErrors = [...recipientErrors, {
          id: 'statement-allocation-total',
          errorOwner: structure?.basicDetails?.name,
          errorType: 'Ownership',
          errorValue: ownership,
        }];
      }
    }

    if (structure?.recipients) {
      recipientErrors = [...recipientErrors, ...BeneficialOwner.getRecipientAllocationErrors(structure.recipients)];
    }

    return recipientErrors;
  }

  constructor(props) {
    super(props);
    this.state = {
      toggleState: {
        isAdditionalExpanded: false,
        isOverrideTopTier: false,
        isTierExpanded: false,
      },
      isEdit: false,
      rowToDelete: null,
      details: {
        accountName: null,
        name: null,
        disregardedEntityName: null,
        giin: null,
        tin: null,
        foreignTin: null,
        taxResidenceCountry: null,
        fatcaStatus: null,
        chapter3RecipientCode: null,
        usNonUsStatus: null,
        address: null,
        city: null,
        stateProvince: null,
        zip: null,
        country: null,
        new: false,
        active: false,
        tinType: null,
        limitationOnBenefits: null,
        treatyBenefits: null,
        allocationIncomeRate: 0,
        accountNumber: null,
        formType: null,
        allocationFromChildren: false,
        isTier: null,
        tierAllocation: 0,
        statementAllocation: 0
      },
      checkboxState: {
        formsDisableImyCheckbox: false,
        allocDisablesImyCheckbox: false,
        formsDisableOwnerCheckbox: false,
        tierAllocDisablesImyCheckbox: false
      },
      tierAllocationChanged: false,
      stmtAllocationChanged: false,
      showUpdateConfirmation: false,
      rowToUpdate: null
    };
  }

  componentDidMount() {
    this.setExpandedTiers();
    this.setFieldStates();
  }

  componentDidUpdate(oldProps) {
    const { isTopTierExpanded, beneficialOwner, statementType, isDeletingRow, tierCollapseStructure } = this.props;
    const isPooled = statementType === StatementTypes.PooledWhdStatement;
    const ownerDetails = isPooled ? beneficialOwner : beneficialOwner.ownerDetails;
    const rowIdentifier = ownerDetails.guid || ownerDetails.name || ownerDetails.accountName;

    if (oldProps && oldProps.isTopTierExpanded !== isTopTierExpanded) {
      this.resetOverrideTopTierCollapse(rowIdentifier, isTopTierExpanded);
    }

    if (oldProps && oldProps.isDeletingRow && !isDeletingRow) {
      this.closeDeleteConfirmation();
    }

    if (oldProps && JSON.stringify(oldProps.tierCollapseStructure) !== JSON.stringify(tierCollapseStructure)) {
      if (tierCollapseStructure[rowIdentifier] != null) {
        this.setExpandedTiers();
      }
    }

    if (!isPooled && oldProps.beneficialOwner.ownerDetails !== ownerDetails) {
      this.setFieldStates();
    }
  }

  static getLOBDescriptionByCode = (code) => {
    const codeValue = (code && parseInt(code, 10)) || null;
    const item = code && codeValue > 0 && LOBCodes.find(x => x.value === codeValue);
    return (codeValue && `${ codeValue < 10 ? '0' : '' }${ codeValue } - ${ (item && item.description) || 'Unknown code' }`) || code || null;
  };

  static getStmtAllocationTitle() {
    return 'WHS Allocation';
  }

  static getAllocationValue(beneficialOwner, decimalPrecision) {
    return BeneficialOwner.parsePrecision(BeneficialOwner.roundPrecision(beneficialOwner.statementAllocation * 100, decimalPrecision), decimalPrecision);
  }

  static getIntermediaryForOwnersValue(beneficialOwner) {
    return beneficialOwner?.recipients?.length;
  }

  static getTierAllocationValue(beneficialOwner, decimalPrecision) {
    return BeneficialOwner.parsePrecision(BeneficialOwner.roundPrecision(beneficialOwner.tierAllocation * 100, decimalPrecision), decimalPrecision);
  }

  static getIntermediaryForText(numberOfOwners) {
    return `${ numberOfOwners } owner${ numberOfOwners === 0 || numberOfOwners > 1 ? 's' : '' }`;
  }

  static getTierOwnershipPercent(beneficialOwner, decimalPrecision) {
    let ownership = null;
    if (beneficialOwner.recipients) {
      ownership = beneficialOwner.recipients.reduce((prev, curr) => prev + (curr.tierAllocation || 0), 0);
    }
    let ownershipPercentValue = BeneficialOwner.toPrecision(ownership * 100, decimalPrecision);
    if (ownershipPercentValue) {
      ownershipPercentValue = parseFloat(ownershipPercentValue);
    }
    return ownershipPercentValue;
  }

  static getTierOwnersCount(beneficialOwner) {
    if (beneficialOwner.recipients) {
      return beneficialOwner.recipients.length;
    }
    return 0;
  }

  onNameChange(value) {
    const { statementType } = this.props;
    const { details } = this.state;
    const isPooled = statementType === StatementTypes.PooledWhdStatement;
    if (isPooled) {
      this.setState({ details: { ...details, accountName: value } });
    } else {
      this.setState({ details: { ...details, name: value } });
    }
  }

  setExpandedTiers() {
    const { tierCollapseStructure, beneficialOwner, statementType } = this.props;

    const isPooled = statementType === StatementTypes.PooledWhdStatement;
    const ownerDetails = isPooled ? beneficialOwner : beneficialOwner.ownerDetails;
    const name = ownerDetails.guid || ownerDetails.name || ownerDetails.accountName;
    const toggleState = tierCollapseStructure && tierCollapseStructure[name];
    if (toggleState) {
      toggleState.isOverrideTopTier = true;
      this.setState({ toggleState });
    }
  }

  setFieldStates() {
    const { statementType, beneficialOwner, decimalPrecision } = this.props;
    const { details, checkboxState } = this.state;

    const isPooled = statementType === StatementTypes.PooledWhdStatement;
    const isDetailed = statementType === StatementTypes.DetailedWhdStatement;
    const isSimple = statementType === StatementTypes.SimpleWhdStatement;
    const isAlt = statementType === StatementTypes.AltWhdStatement;

    const ownerDetails = Object.assign(isPooled ? beneficialOwner : beneficialOwner.ownerDetails, {});
    const name = ownerDetails.name || ownerDetails.accountName;
    const chapter3Item = Chapter3Status.findByName(ownerDetails.chapter3RecipientCode);
    const chapter4Item = Chapter4Status.findByName(ownerDetails.fatcaStatus);
    const allocationValue = BeneficialOwner.getAllocationValue(beneficialOwner, decimalPrecision);
    const statementAllocation = BeneficialOwner.getAllocationValue(beneficialOwner, decimalPrecision);
    const tierAllocationValue = BeneficialOwner.getTierAllocationValue(beneficialOwner, decimalPrecision);

    details.chapter3RecipientCode = (chapter3Item && chapter3Item.name) || null;
    details.fatcaStatus = (chapter4Item && chapter4Item.name) || null;
    details.allocationIncomeRate = allocationValue ? allocationValue * 1 : null;
    details.statementAllocation = statementAllocation ? statementAllocation * 1 : null;
    details.tierAllocation = tierAllocationValue ? tierAllocationValue * 1 : null;

    details.allocationFromChildren = BeneficialOwner.isAllocationFromChildren(beneficialOwner);

    if (isPooled) {
      details.accountName = name;
    }

    if (!isPooled) {
      details.name = name;
      details.disregardedEntityName = ownerDetails.disregardedEntityName;
      details.giin = ownerDetails.giin;
      details.isTier = ownerDetails.isTier;

      if (isDetailed || isSimple) {
        details.tin = ownerDetails.tin;
        details.foreignTin = ownerDetails.foreignTIN;
        details.address = ownerDetails.address;
      }
      if (isDetailed || isSimple || isAlt) {
        details.taxResidenceCountry = ownerDetails.taxResidenceCountry;
      }

      if (isDetailed || isSimple || isPooled) {
        details.usNonUsStatus = ownerDetails.usNonUsStatus;
      }

      if (isDetailed) {
        details.city = ownerDetails.city;
        details.stateProvince = ownerDetails.stateProvince;
        details.zip = ownerDetails.zip;
        details.country = ownerDetails.country;
        details.new = ownerDetails.new;
        details.active = ownerDetails.active;
        details.tinType = ownerDetails.tinType;
        details.treatyBenefits = ownerDetails.treatyBenefits;
        details.limitationOnBenefits = ownerDetails.limitationOnBenefits;
        details.accountNumber = ownerDetails.accountNumber;
        details.formType = ownerDetails.formType;
      }

      const formInt = details.formType && parseInt(details.formType, 10);
      const detailsFormType = formInt && FormTypes.getByValue(formInt);

      checkboxState.formsDisableImyCheckbox = !!(detailsFormType && detailsFormType.family !== 'w8imy');
      checkboxState.allocDisablesImyCheckbox = !details.allocationFromChildren && !!details.allocationIncomeRate;
      checkboxState.formsDisableOwnerCheckbox = !!(detailsFormType && detailsFormType.family === 'w8imy');
    }

    this.setState({ details, checkboxState });
  }

  getTierRecipientCount() {
    const { beneficialOwner } = this.props;
    return beneficialOwner?.recipients ? beneficialOwner?.recipients.filter(r => !r.recipients).length : 0;
  }

  getTierIntermediaryCount() {
    const { beneficialOwner } = this.props;
    return beneficialOwner?.recipients ? beneficialOwner?.recipients.filter(r => r.recipients).length : 0;
  }

  ownerHasValidationErrors = (recipient) => {
    const { validationResult } = this.props;

    if (!validationResult?.lines || validationResult.lines.length === 0) {
      return false;
    }

    const lineNo = recipient && (recipient.lineNo || (recipient.ownerDetails && recipient.ownerDetails.lineNo > 0 && recipient.ownerDetails.lineNo));
    const lineValidation = lineNo > 0 && validationResult.lines.find(result => result.lineNumber === lineNo);

    return lineNo > 0
      && (ValidationInspector.hasRejections(lineValidation)
        || ValidationInspector.hasWarnings(lineValidation));
  };

  getOwnerValidationState = (recipient) => {
    const { validationResult } = this.props;

    if (!validationResult?.lines || validationResult.lines.length === 0) {
      return false;
    }

    const lineNo = recipient && (recipient.lineNo || (recipient.ownerDetails && recipient.ownerDetails.lineNo > 0 && recipient.ownerDetails.lineNo));
    const lineValidation = lineNo > 0 && validationResult.lines.find(result => result.lineNumber === lineNo);

    return lineNo > 0 ? {
      hasRejections: ValidationInspector.hasRejections(lineValidation),
      hasWarnings: ValidationInspector.hasWarnings(lineValidation)
    } : null;
  };

  tierHasValidationErrors = () => {
    const { beneficialOwner, validationResult } = this.props;
    if (!beneficialOwner.recipients || !validationResult?.lines || validationResult.lines.length === 0) {
      return false;
    }

    if (this.ownerHasValidationErrors(beneficialOwner)) {
      return true;
    }

    // This filter ensures only direct owners and not sub-layers are checked
    return beneficialOwner.recipients.some(recipient => recipient
      && !Object.prototype.hasOwnProperty.call(recipient, 'recipients')
      && this.ownerHasValidationErrors(recipient));
  };

  toggleExpandedAdditionalDetails = () => {
    const { updateToggledCollapsibleTiers, beneficialOwner, statementType } = this.props;
    const { toggleState } = this.state;
    const isPooled = statementType === StatementTypes.PooledWhdStatement;
    const ownerDetails = isPooled ? beneficialOwner : beneficialOwner.ownerDetails;
    const rowIdentifier = ownerDetails.guid || ownerDetails.name || ownerDetails.accountName;

    toggleState.isAdditionalExpanded = !toggleState.isAdditionalExpanded;
    this.setState({ toggleState });
    updateToggledCollapsibleTiers(rowIdentifier, toggleState);
  };

  toggleCollapseTier = () => {
    const { isTopTierExpanded, updateToggledCollapsibleTiers, beneficialOwner, statementType } = this.props;
    const { toggleState } = this.state;

    const { isTierExpanded, isOverrideTopTier } = toggleState;
    const isPooled = statementType === StatementTypes.PooledWhdStatement;
    const ownerDetails = isPooled ? beneficialOwner : beneficialOwner.ownerDetails;
    const rowIdentifier = ownerDetails.guid || ownerDetails.name || ownerDetails.accountName;

    if (!isOverrideTopTier) {
      toggleState.isOverrideTopTier = true;
      toggleState.isTierExpanded = !isTopTierExpanded;
    } else {
      toggleState.isTierExpanded = !isTierExpanded;
    }

    this.setState({ toggleState });
    updateToggledCollapsibleTiers(rowIdentifier, toggleState);
  };

  onIncomeAllocationChange = (value) => {
    const { beneficialOwner, decimalPrecision } = this.props;
    const { details, checkboxState } = this.state;
    const result = this.getImyCheckboxState(null, value, true);

    const stmtAllocValue = BeneficialOwner.getAllocationValue(beneficialOwner, decimalPrecision);
    const newIntValue = parseFloat(value);
    const stmtAllocationChanged = stmtAllocValue !== newIntValue;

    if (value >= 0 && value <= 100) {
      this.setState({
        details: { ...details,
          statementAllocation: value,
        },
        checkboxState: {
          ...checkboxState,
          allocDisablesImyCheckbox: result[2],
        },
        stmtAllocationChanged
      });
    }
  };

  onTierAllocationChange = (value) => {
    const { beneficialOwner, decimalPrecision } = this.props;
    const { details, checkboxState } = this.state;
    const result = this.getImyCheckboxState(null, null, null, value, true);

    const tierAllocValue = BeneficialOwner.getTierAllocationValue(beneficialOwner, decimalPrecision);
    const newIntValue = parseFloat(value);
    const tierAllocationChanged = tierAllocValue !== newIntValue;

    if (value >= 0 && value <= 100) {
      this.setState({
        details: {
          ...details,
          tierAllocation: value
        },
        checkboxState: {
          ...checkboxState,
          tierAllocDisablesImyCheckbox: result[3],
        },
        tierAllocationChanged
      });
    }
  };

  addOwner = (topAccountId, parentGuid, isLayer) => {
    const { addBeneficialOwner, updateToggledCollapsibleTiers } = this.props;
    const { toggleState } = this.state;
    if (!toggleState.isOverrideTopTier) {
      toggleState.isOverrideTopTier = true;
    }

    toggleState.isTierExpanded = true;
    this.setState({ toggleState });
    updateToggledCollapsibleTiers(parentGuid, toggleState);

    const preventExpand = true;
    addBeneficialOwner(topAccountId, parentGuid, isLayer, preventExpand);
  };

  // eslint-disable-next-line class-methods-use-this
  doesCh3AllowAllocationChange = (ch3Name, boRecipients) => {
    if (ch3Name && (!boRecipients || boRecipients.length === 0)) {
      const chapter3Item = Chapter3Status.findByName(ch3Name);
      if (chapter3Item && chapter3Item.value) {
        const allowedCh3Elections = [3, 5, 9, 11, 12, 35, 39];

        if (allowedCh3Elections.includes(chapter3Item.value)) {
          return true;
        }
      }
    }
    return false;
  };

  doesCh3AllowImyCheckbox = (ch3Code) => {
    const { details } = this.state;
    const codeToUse = ch3Code || details.chapter3RecipientCode;

    if (codeToUse) {
      const ch3Election = Chapter3Status.findByName(codeToUse);
      if (ch3Election && ch3Election.value) {
        const allowedCh3Elections = [8, 10];

        if (allowedCh3Elections.includes(ch3Election.value)) {
          return true;
        }
      }
    }
    return false;
  };

  getImyCheckboxState = (formType, allocation, allocationChanged, tierAllocation, tierAllocationChanged) => {
    const { details } = this.state;
    let isTier;

    const formTypeFamily = formType ? FormTypes.getByValue(formType) : details.formType && FormTypes.getByValue(details.formType);

    const imyCheckBoxResult = this.getImyCheckboxSettings(formTypeFamily, allocation, allocationChanged, tierAllocation, tierAllocationChanged);

    const formsDisableImyCheckbox = imyCheckBoxResult[0];
    const allocDisablesImyCheckbox = imyCheckBoxResult[1];
    const tierAllocDisablesImyCheckbox = imyCheckBoxResult[2];

    if (formsDisableImyCheckbox || allocDisablesImyCheckbox || tierAllocDisablesImyCheckbox) {
      isTier = false;
    }

    const formsDisableOwnerCheckbox = Number.isNaN(formTypeFamily) ? false : this.getOwnerCheckboxSettings(formTypeFamily);

    if (formsDisableOwnerCheckbox) {
      isTier = true;
    }

    return [isTier, formsDisableImyCheckbox, allocDisablesImyCheckbox, formsDisableOwnerCheckbox, tierAllocDisablesImyCheckbox];
  };

  // eslint-disable-next-line class-methods-use-this
  getOwnerCheckboxSettings = formTypeFamily => !!(formTypeFamily && formTypeFamily.family === 'w8imy');

  getImyCheckboxSettings = (formTypeFamily, allocation, allocationChanged, tierAllocation, tierAllocationChanged) => {
    const { details } = this.state;
    const formsDisableImyCheckbox = !!(formTypeFamily && formTypeFamily.family !== 'w8imy');
    const allocDisablesImyCheckbox = allocationChanged ? !!allocation : !!details.allocationIncomeRate;
    const tierAllocDisablesImyCheckbox = tierAllocationChanged ? !!tierAllocation : !!details.tierAllocation;

    return [formsDisableImyCheckbox, allocDisablesImyCheckbox, tierAllocDisablesImyCheckbox];
  };

  onFormTypeChange = (e) => {
    const { details, checkboxState } = this.state;
    const formType = parseInt(e.target.value, 10);

    const intermediaryCheckboxResult = this.getImyCheckboxState(formType, null);

    this.setState({
      details: { ...details,
        formType,
        isTier: e.target.value ? intermediaryCheckboxResult[0] : null
      },
      checkboxState: { ...checkboxState,
        formsDisableImyCheckbox: intermediaryCheckboxResult[1],
        formsDisableOwnerCheckbox: intermediaryCheckboxResult[3] } });
  };

  onCh3Change = (e) => {
    const { details } = this.state;
    const ch3Result = this.doesCh3AllowImyCheckbox(e.target.value);
    const clearIsTier = !ch3Result;

    this.setState({ details: { ...details,
      chapter3RecipientCode: (e.target.value && Chapter3Status.findByName(e.target.value).name) || '',
      isTier: clearIsTier ? null : details.isTier
    } });
  };

  displayStatementAllocationValue = (beneficialOwner) => {
    const { decimalPrecision } = this.props;
    const allocation = BeneficialOwner.getAllocationValue(beneficialOwner, decimalPrecision);
    return BeneficialOwner.toPrecision(allocation, decimalPrecision);
  };

  displayTierAllocationValue = (beneficialOwner) => {
    const { decimalPrecision } = this.props;
    const allocation = BeneficialOwner.getTierAllocationValue(beneficialOwner, decimalPrecision);
    return BeneficialOwner.toPrecision(allocation, decimalPrecision);
  };

  submitData() {
    const { beneficialOwner, statementType, topAccountId, dispatch, decimalPrecision } = this.props;
    const { details, isEdit } = this.state;

    const isPooled = statementType === StatementTypes.PooledWhdStatement;

    const ownerDetails = isPooled ? beneficialOwner : beneficialOwner.ownerDetails;
    const currentAccountName = ownerDetails.name || ownerDetails.accountName;
    const boCopy = Object.assign(isPooled ? beneficialOwner : beneficialOwner.ownerDetails);
    let updatedObject = Object.assign({}, boCopy);
    updatedObject = Object.assign(updatedObject, details);

    let allocationRate = details.allocationIncomeRate ? details.allocationIncomeRate / 100 : null;
    if (allocationRate) {
      allocationRate = parseFloat(allocationRate.toFixed(2 + (decimalPrecision ?? 8)));
      details.allocationIncomeRate = (allocationRate * 100);
    }

    let stmtAllocationRate = details.statementAllocation ? details.statementAllocation / 100 : null;
    if (stmtAllocationRate) {
      stmtAllocationRate = parseFloat(stmtAllocationRate.toFixed(2 + (decimalPrecision ?? 8)));
      details.statementAllocation = (stmtAllocationRate * 100);
    }

    let tierAllocation = details.tierAllocation ? details.tierAllocation / 100 : null;
    if (tierAllocation) {
      tierAllocation = parseFloat(tierAllocation.toFixed(2 + (decimalPrecision ?? 8)));
      details.tierAllocation = (tierAllocation * 100);
    }

    updatedObject.allocationIncomeRate = allocationRate;
    updatedObject.tierAllocation = tierAllocation;
    updatedObject.statementAllocation = stmtAllocationRate;

    if (details.allocationFromChildren) {
      updatedObject.allocationIncomeRate = null;
    }

    const isOrigCh3ImyStatus = boCopy.chapter3RecipientCode ? Chapter3Status.findByName(boCopy.chapter3RecipientCode)?.isImyStatus : null;
    const isNewCh3ImyStatus = updatedObject.chapter3RecipientCode ? Chapter3Status.findByName(updatedObject.chapter3RecipientCode)?.isImyStatus : null;
    const isOrigImyFormFamily = boCopy.formType != null ? FormTypes.getByValue(boCopy.formType).family : null;
    const isNewImyFormFamily = updatedObject.formType != null ? FormTypes.getByValue(updatedObject.formType).family : null;

    const isOrigImy = boCopy.isTier === true || (boCopy.isTier !== false && isOrigCh3ImyStatus === true) || (isOrigCh3ImyStatus === null && isOrigImyFormFamily === 'w8imy');
    const isNewImy = updatedObject.isTier === true || (updatedObject.isTier !== false && isNewCh3ImyStatus === true) || (isNewCh3ImyStatus === null && isNewImyFormFamily === 'w8imy');

    const rowUpdate = {
      guid: ownerDetails.guid,
      name: currentAccountName,
      type: statementType,
      owner: !isPooled ? updatedObject : null,
      account: isPooled ? updatedObject : null
    };

    if (isOrigImy && !isNewImy) {
      // It's indicating BO now via Chapter 3, Form Type and/or tick box.
      const countOwners = recipients => recipients.reduce((acc, recipient) => {
        if (recipient.recipients) {
          return acc + 1 + countOwners(recipient.recipients);
        }
        return acc + 1;
      }, 0);
      const ownerCount = countOwners(beneficialOwner.recipients || []);

      if (ownerCount > 0) {
        this.setState({ showUpdateConfirmation: true, rowToUpdate: { details, payload: rowUpdate, owners: ownerCount } });
        return;
      }
    }

    dispatch(updateStatementRow(topAccountId, rowUpdate));
    this.setState({ isEdit: !isEdit, details });
    this.setState({ tierAllocationChanged: false, stmtAllocationChanged: false });
  }

  confirmUpdate() {
    const { dispatch, topAccountId } = this.props;
    const { rowToUpdate, isEdit } = this.state;

    dispatch(updateStatementRow(topAccountId, rowToUpdate.payload));
    this.setState({ isEdit: !isEdit, details: rowToUpdate.details });
    this.setState({ tierAllocationChanged: false, stmtAllocationChanged: false, showUpdateConfirmation: false });
  }

  cancelUpdate() {
    this.setState({ rowToUpdate: null, showUpdateConfirmation: false });
  }

  resetOverrideTopTierCollapse(rowIdentifier, isTopExpanded) {
    const { updateToggledCollapsibleTiers } = this.props;
    const { toggleState } = this.state;
    toggleState.isTierExpanded = isTopExpanded;
    toggleState.isOverrideTopTier = false;
    this.setState({ toggleState });
    updateToggledCollapsibleTiers(rowIdentifier, toggleState);
  }

  deleteRow() {
    const { dispatch, topAccountId } = this.props;
    const { rowToDelete } = this.state;
    dispatch(deleteStatementRow(topAccountId, rowToDelete));
  }

  openDeleteConfirmation() {
    const { statementType, beneficialOwner } = this.props;
    const ownerDetails = statementType === StatementTypes.PooledWhdStatement ? beneficialOwner : beneficialOwner.ownerDetails;

    this.setState({ rowToDelete: {
      guid: ownerDetails.guid,
      type: statementType,
    } });
  }

  closeDeleteConfirmation() {
    this.setState({ rowToDelete: null });
  }

  isBoVisible(recipient, isImyLayer, selectedPills) {
    const missingForm = recipient?.basicDetails?.formId === null;
    const invalidForm = !missingForm && (recipient?.basicDetails?.formIsValid === false || recipient?.basicDetails?.formIsValid == null);
    const formInReview = recipient?.basicDetails?.workflowStage >= 4 && recipient.basicDetails?.workflowStage <= 9;
    const validationState = this.getOwnerValidationState(recipient);
    const hasRejections = validationState?.hasRejections;
    const hasWarnings = validationState?.hasWarnings;
    const missingAllocation = (recipient.ownerDetails?.allocationIncomeRate == null || recipient.ownerDetails?.allocationIncomeRate === 0) && !isImyLayer;

    const result = (missingForm && selectedPills.missingForms)
      || (invalidForm && selectedPills.invalidForms)
      || (formInReview && selectedPills.inReviewForms)
      || (hasRejections && selectedPills.rejections)
      || (hasWarnings && selectedPills.warnings)
      || (missingAllocation && selectedPills.missingAllocation);
    return result;
  }

  render() {
    const {
      beneficialOwner,
      decimalPrecision,
      getChapter3DescriptionByName,
      getChapter4DescriptionByName,
      idx,
      isParentTierExpanded,
      isTopTierExpanded,
      openChangeParentModal,
      parents,
      statementType,
      permissions,
      dispatch,
      openAccountWithholdingModal,
      openAddFormModal,
      addBeneficialOwner,
      topAccountId,
      isSavingStatement,
      isJointAccount,
      validationResult,
      validationLineNo,
      openPooledRatesModal,
      updateToggledCollapsibleTiers,
      tierCollapseStructure,
      isDeletingRow,
      isStructureValid,
      isAddingNewBeneficalOwner,
      statementVersion,
      hideTierAllocationValue,
      selectedPills
    } = this.props;

    const {
      toggleState: {
        isAdditionalExpanded,
        isTierExpanded,
        isOverrideTopTier
      },
      isEdit,
      rowToDelete,
      details,
      checkboxState,
      tierAllocationChanged,
      stmtAllocationChanged,
      showUpdateConfirmation,
      rowToUpdate
    } = this.state;

    const isAlt = statementType === StatementTypes.AltWhdStatement;
    const isDetailed = statementType === StatementTypes.DetailedWhdStatement;
    const isSimple = statementType === StatementTypes.SimpleWhdStatement;
    const isPooled = statementType === StatementTypes.PooledWhdStatement;

    const showingVersion = statementVersion !== null && typeof statementVersion !== 'undefined';

    const ownerDetails = isPooled ? beneficialOwner : beneficialOwner.ownerDetails;
    const name = ownerDetails.name || ownerDetails.accountName;
    const detailsName = isPooled ? details.accountName : details.name;
    const levelParents = [...parents];
    const formType = FormTypes.getByValue(ownerDetails.formType);
    const uploadedFormType = beneficialOwner?.basicDetails?.formType && FormTypes.getByValue(beneficialOwner.basicDetails.formType);
    const chapter3Details = Chapter3Status.findByName(details.chapter3RecipientCode);
    const isImyLayer = (beneficialOwner.recipients ?? []).length > 0
      || details.isTier === true
      || (details.isTier === null && !details.chapter3RecipientCode && formType === FormTypes.w8imy)
      || (details.isTier === null && details.chapter3RecipientCode && chapter3Details?.isImyStatus && ![8, 10].includes(chapter3Details?.value));
    const allowLayerAllocationChange = this.doesCh3AllowAllocationChange(details.chapter3RecipientCode, beneficialOwner.recipients);

    const isTopTierCollapsed = isImyLayer && !isOverrideTopTier && !isTopTierExpanded;
    const isTierCollapsed = isTopTierCollapsed || (isImyLayer && isOverrideTopTier ? !isTierExpanded : !isParentTierExpanded);
    const validationResultLine = validationResult && validationResult.lines && validationResult.lines.length >= 0 && validationResult.lines.find(result => result.lineNumber === validationLineNo);
    const beneficialOwnerObject = beneficialOwner;
    const { basicDetails } = beneficialOwnerObject;
    const matchedFormAccountName = isJointAccount ? beneficialOwnerObject?.basicDetails?.customerName?.trim() : beneficialOwnerObject?.basicDetails?.name?.trim();
    const matchedFormEntityName = beneficialOwnerObject && beneficialOwnerObject.basicDetails?.formEntityName?.trim();
    const isAccountNameMismatch = matchedFormAccountName && (BeneficialOwner.cleanString(matchedFormAccountName).toLowerCase() !== BeneficialOwner.cleanString(name)?.trim().toLowerCase());
    const isFormEntityNameMismatch = matchedFormEntityName && (BeneficialOwner.cleanString(matchedFormEntityName).toLowerCase() !== BeneficialOwner.cleanString(name)?.trim().toLowerCase());
    const hasFormUploaded = beneficialOwnerObject && beneficialOwnerObject.basicDetails && beneficialOwnerObject.basicDetails.formId !== null;
    const ownerFormId = hasFormUploaded && beneficialOwnerObject.basicDetails.formId;
    const ownerAccountId = beneficialOwnerObject.basicDetails && beneficialOwnerObject.basicDetails.accountId;
    const canEdit = AccessControl.hasPermission(permissions, [ActivityPerms.multiTierMgrEditWhs]) && !showingVersion;
    const canUploadPermission = AccessControl.hasPermission(permissions, [ActivityPerms.scannedUpload, ActivityPerms.scannedWhdStmtBoUpload], true);
    const tierRecipients = this.getTierRecipientCount();
    const tierIntermediaries = this.getTierIntermediaryCount();
    const canSetIntermediaryCheckbox = this.doesCh3AllowImyCheckbox();

    const disabledOwnerToolTipText = checkboxState.formsDisableOwnerCheckbox ? 'Beneficiary Owners require W-8BEN-E or W-8ECI forms.' : null;
    const disableIntermediaryCheckbox = !!((checkboxState.formsDisableImyCheckbox || checkboxState.allocDisablesImyCheckbox));
    let disabledIntermediaryToolTipText = null;

    if (checkboxState.formsDisableImyCheckbox) {
      disabledIntermediaryToolTipText = 'To be an Intermediary requires a W8-IMY form.';
    } else if (checkboxState.allocDisablesImyCheckbox) {
      disabledIntermediaryToolTipText = 'Intermediaries cannot have a direct % allocation.';
    }

    const pooledRecipient = beneficialOwner;

    const buildImyTierObject = (item) => {
      if (!item) {
        return null;
      }
      const formObject = {
        accountId: item.accountId,
        accountName: item.name,
        accountNumber: item.accountNumber,
        customerDbId: item.customerDbId,
        customerId: item.customerIdentifier,
        customerName: item.customerName,
      };

      return formObject;
    };

    const buildFormObject = (item) => {
      if (item === undefined) {
        return null;
      }
      const formTypeValue = item && item.formType && FormTypes.getByValue(item.formType);
      const formObject = {
        accountId: item.accountId,
        id: item.formId,
        name: item.name,
        customerDbId: item.customerDbId,
        customerName: item.customerName,
        formId: item.formId,
        formType: formTypeValue && formTypeValue.code,
        workflowStatus: item.workflowStatus,
        workflowUsername: item.workflowUsername ? item.workflowUsername : 'N/A',
        workflowUserFullName: item.workflowUserFullName
      };
      return formObject;
    };

    if (isImyLayer) {
      levelParents.push(name);
    }

    const {
      isFormMissing,
      isFormValid,
      isFormInvalid,
      isFormInReviewStage,
      isBoMissingAllocation,
      warningCount,
      rejectionCount
    } = BeneficialOwner.checkRecipientFilterMatches(beneficialOwner, validationResult, isPooled);

    const allFalse = Object.values(selectedPills).every(value => value === false);
    const missingForm = isFormMissing;
    const validForm = isFormValid;
    const invalidForm = isFormInvalid;
    const formInReview = isFormInReviewStage;
    const formComplete = basicDetails?.workflowStage === 100;
    const hasRejections = rejectionCount > 0;
    const hasWarnings = warningCount > 0;
    const missingAllocation = isBoMissingAllocation;
    const isBoVisible = allFalse || this.isBoVisible({ basicDetails, ownerDetails, lineNo: validationLineNo }, isImyLayer, selectedPills);

    const isImyHeaderVisible = isBoVisible || beneficialOwner?.recipients?.filter(r => this.isBoVisible(r, isImyLayer, selectedPills)).length > 0;

    return (isImyLayer || !isTierCollapsed) && <React.Fragment>
      <div id={ !isPooled ? beneficialOwner.ownerDetails.guid : beneficialOwner.guid } className={ isBoVisible ? 'beneficiary-owner' : '' }>
        { (isBoVisible || isImyHeaderVisible) && <div className={ `statement-breadcrumbs ${ !isImyLayer ? 'indent' : '' }` }>
          <div className="breadcrumbs">
            /{ levelParents && levelParents.length > 0 && levelParents.map(x => <React.Fragment key={ [...parents, x] }> <span className="breadcrumb-item">{ x }</span> /</React.Fragment>) }
          </div>
          { isImyLayer && !showingVersion && <div className="button-row">
            { canEdit && <button
              type="button"
              onClick={ () => openChangeParentModal(beneficialOwner.ownerDetails.guid, beneficialOwner.basicDetails, idx) }
              className="btn link-button"
            ><span className="icon icon-list-position" />Change Parent</button> }
            <button type="button" onClick={ this.toggleCollapseTier } className="btn link-button"><span className="icon icon-resize-vert" />{ isTierCollapsed ? 'Open' : 'Collapse' } Tier</button>
          </div> }
        </div> }
        { isImyLayer && isImyHeaderVisible && <div className={ `panel mb-2 imy-card${ (this.tierHasValidationErrors() && ' validation-highlight') || '' }` } data-testid={ removeInvalidHtmlChars(`${ levelParents }`).toLowerCase() }>
          <div className="columns">
            <div className="col-12">
              <div className="columns imy-layer">
                <div className="col-6 align-vertical-middle">
                  <div className="owner-header">
                    <h2 className="inline mr-2rem value">{ valueOrEmpty(name) }</h2>
                    { missingForm && (<span className="status-pill red flat">Form Missing</span>) }
                    { formInReview && (<span className="status-pill orange flat">Form In Review</span>) }
                    { formComplete && (<span type="button" className="status-pill grey flat">Form Complete</span>) }
                    { invalidForm && (<span type="button" className="status-pill red flat">Form Invalid</span>) }
                    { validForm && (<span type="button" className="status-pill grey flat">Form Valid</span>) }
                    { hasRejections && (<button type="button" className="status-pill red flat">{ rejectionCount } Rejection(s)</button>) }
                    { hasWarnings && (<button type="button" className="status-pill orange flat">{ warningCount } Warning(s)</button>) }
                  </div>
                  <div className="owner-header value mr-2">
                    { tierRecipients } { isPooled ? 'Pool' : 'Owner' }
                    { tierRecipients === 0 || tierRecipients > 1 ? 's' : '' }
                  </div>
                  { !isPooled && <div className="owner-header value">/ { tierIntermediaries } Intermediar{ tierIntermediaries === 0 || tierIntermediaries > 1 ? 'ies' : 'y' }</div> }
                </div>
                <div className="col-2 align-vertical-center">
                  <div className="owner-header value">{ this.displayStatementAllocationValue(beneficialOwner) }% WHS Allocation</div>
                </div>
                <div className="col-4 align-vertical-middle">
                  <div className="col-4 align-vertical-center">
                    { !isPooled && hasFormUploaded && canUploadPermission && !showingVersion && <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={ () => openAddFormModal(buildImyTierObject(beneficialOwnerObject.basicDetails)) }
                      data-testid="btn-upload-forms-bo"
                    >
                      <i className="icon icon-2x icon-upload mr-2" />Upload Form(s)
                    </button> }
                  </div>
                  { canEdit && <div className="col-4 align-vertical-center">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={ isAddingNewBeneficalOwner }
                      onClick={ () => this.addOwner(topAccountId, beneficialOwner.ownerDetails.guid, false) }
                      data-testid={ isPooled ? 'btn-add-pool-bo' : 'btn-add-beneficial-owner-bo' }
                    >
                      <i className="icon icon-2x icon-plus mr-2" />{ isPooled ? 'Add Pool' : 'Add Owner' }
                    </button>
                  </div> }
                  { !isPooled && canEdit && <div className="col-4 align-vertical-center">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      disabled={ isAddingNewBeneficalOwner }
                      onClick={ () => this.addOwner(topAccountId, beneficialOwner.ownerDetails.guid, true) }
                      data-testid="btn-add-imy-tier-bo"
                    >
                      <i className="icon icon-2x icon-plus mr-2" />Add IMY Tier
                    </button>
                  </div> }
                </div>
              </div>
            </div>
          </div>
        </div> }
        {!isTierCollapsed && isBoVisible
        && <div className={ `panel statement-line-panel${ (this.ownerHasValidationErrors(beneficialOwner) && ' validation-highlight') || '' }` } data-testid={ removeInvalidHtmlChars(`${ levelParents } ${ name }`).toLowerCase() }>
          <div className="columns">
            <div className="col-9">
              <div className="columns">
                { (!isImyLayer || allowLayerAllocationChange) && <div className="col-8">
                  <div className="owner-header">
                    <h2 className="inline mr-2rem">{ name }</h2>
                    { missingForm && (<span className="status-pill red flat">Form Missing</span>) }
                    { formInReview && (<span className="status-pill orange flat">Form In Review</span>) }
                    { formComplete && (<span className="status-pill grey flat">Form Complete</span>) }
                    { invalidForm && (<span className="status-pill red flat">Form Invalid</span>) }
                    { validForm && (<span className="status-pill grey flat">Form Valid</span>) }
                    { hasRejections && (<button type="button" className="status-pill red flat">{ rejectionCount } Rejection(s)</button>) }
                    { hasWarnings && (<button type="button" className="status-pill orange flat">{ warningCount } Warning(s)</button>) }
                    { missingAllocation && (<span className="status-pill orange flat">% Allocation Missing</span>) }
                  </div>
                </div> }
                { isDetailed && <div className="col-4">
                  <div className="owner-uniqueid"><h3>
                    <div className="columns">
                      <div className={ ` ${ !isEdit ? 'col-12' : 'col-3' } panel-item-name` }>Unique ID:&nbsp;{ !isEdit && <span className="value">{ valueOrEmpty(ownerDetails.accountNumber) }</span> }</div>
                      { isEdit && <input type="text" className="col-9 form-input" value={ details.accountNumber || '' } onChange={ e => this.setState({ details: { ...details, accountNumber: e.target.value } }) } /> }
                    </div>
                  </h3>
                  </div>
                </div> }
              </div>
              <div className="columns">
                <div className="col-6">
                  <div className="columns">
                    <div className="col-5 panel-item-name">Name</div>
                    { !isEdit && <div className="col-7">{ name }</div> }
                    { isEdit && <input type="text" className=" col-5 form-input" onChange={ e => this.onNameChange(e.target.value) } value={ detailsName || '' } data-testid="field-name-bo" /> }
                    { !isPooled && <React.Fragment>
                      { isAccountNameMismatch && <React.Fragment>
                        <div className="col-5 italic">Account name mis-match</div><div className="col-7 italic">{ valueOrEmpty(matchedFormAccountName) }</div>
                      </React.Fragment>}
                      { isFormEntityNameMismatch && <React.Fragment>
                        <div className="col-5 italic">Form name mis-match</div><div className="col-7 italic">{ valueOrEmpty(beneficialOwner?.basicDetails?.formEntityName) }</div>
                      </React.Fragment>}
                      <div className="col-5 panel-item-name" data-testid="field-business-name-bo">Business (DRE) Name</div>
                      { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.disregardedEntityName) }</div> }
                      { isEdit
                        && <input
                          type="text"
                          className=" col-5 form-input"
                          onChange={ e => this.setState({ details: { ...details, disregardedEntityName: e.target.value } }) }
                          value={ details.disregardedEntityName || '' }
                          data-testid="field-disregarded-entity-name-bo"
                        /> }
                      <div className="col-5 panel-item-name">GIIN</div>
                      { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.giin) }</div> }
                      { isEdit && <input type="text" className=" col-5 form-input" onChange={ e => this.setState({ details: { ...details, giin: e.target.value } }) } value={ details.giin || '' } data-testid="field-giin-bo" /> }
                      { (isDetailed || isSimple) && <React.Fragment>
                        <div className="col-5 panel-item-name">U.S. TIN</div>
                        { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.tin) }</div> }
                        { isEdit && <input type="text" className=" col-5 form-input" onChange={ e => this.setState({ details: { ...details, tin: e.target.value } }) } value={ details.tin || '' } data-testid="field-us-tin-bo" /> }
                        <div className="col-5 panel-item-name">Foreign TIN</div>
                        { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.foreignTIN) }</div> }
                        { isEdit && <input type="text" className=" col-5 form-input" onChange={ e => this.setState({ details: { ...details, foreignTin: e.target.value } }) } value={ details.foreignTin || '' } data-testid="field-foreign-tin-bo" /> }
                      </React.Fragment> }
                    </React.Fragment> }
                  </div>
                </div>
                <div className="col-6">
                  <div className="columns">
                    { (isDetailed || isSimple || isAlt) && <React.Fragment>
                      <div className="col-5 panel-item-name">Country of Tax Residence</div>
                      { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.taxResidenceCountry) }</div> }
                      { isEdit && <JurisdictionDropDown value={ details.taxResidenceCountry || '' } onChange={ value => this.setState({ details: { ...details, taxResidenceCountry: value } }) } data-testid="dropdown-tax-res-country-bo" /> }
                    </React.Fragment> }
                    { (isDetailed || isSimple || isPooled) && <React.Fragment>
                      <div className="col-5 panel-item-name">U.S. / Non-U.S. Status</div>
                      { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.usNonUsStatus) }</div> }
                      { isEdit && <select className="col-5 form-select" value={ details.usNonUsStatus || '' } onChange={ e => this.setState({ details: { ...details, usNonUsStatus: e.target.value } }) } data-testid="dropdown-us-non-us-status-bo">
                        <option value="">Please select</option>
                        <option>Intermediary</option>
                        <option>Foreign Person</option>
                        <option>Foreign Entity - Exempt</option>
                        <option>Foreign Entity - NonExempt</option>
                        <option>US Individual</option>
                        <option>US Entity - Exempt</option>
                        <option>US Entity - NonExempt</option>
                      </select> }
                    </React.Fragment> }
                    <div className="col-5 panel-item-name">FATCA Status</div>
                    { !isEdit && <div className="col-7">{ valueOrEmpty(getChapter4DescriptionByName(ownerDetails.fatcaStatus)) }</div> }
                    { isEdit && <select
                      className="col-5 form-select"
                      onChange={ e => this.setState({ details: { ...details, fatcaStatus: Chapter4Status.findByName(e.target.value).name || '' } }) }
                      defaultValue={ (details.fatcaStatus && details.fatcaStatus) || '' }
                      data-testid="dropdown-chapter-4-status-bo"
                    >
                      <option value="">Please select</option>
                      { Chapter4Status.filter(x => !isPooled || (isPooled && x.whsPooledStatus)).map(x => (
                        <option key={ x.value } value={ x.name }>{ x.description } - { x.value }</option>
                      )) }
                    </select> }
                    <div className="col-5 panel-item-name">Chapter 3 Recipient Code</div>
                    { !isEdit && <div className="col-7">{ valueOrEmpty(getChapter3DescriptionByName(ownerDetails.chapter3RecipientCode)) }</div> }
                    { isEdit && <select
                      className="col-5 form-select"
                      onChange={ e => this.onCh3Change(e) }
                      defaultValue={ (details.chapter3RecipientCode && details.chapter3RecipientCode) || '' }
                      data-testid="dropdown-chapter-3-reci-code-bo"
                    >
                      <option value="">Please select</option>
                      { Chapter3Status.filter(x => !isPooled || (isPooled && x.whsPooledStatus)).map(x => (
                        <option key={ x.value } value={ x.name }>{ x.description } - { x.value }</option>
                      )) }
                    </select>}
                    { isEdit && canSetIntermediaryCheckbox && <React.Fragment>
                      <div className="col-5 panel-item-name" />
                      <div className="col-5">
                        <label className={ `form-checkbox inline ${ disableIntermediaryCheckbox ? 'tooltip' : '' }` } data-tooltip={ disabledIntermediaryToolTipText }>
                          <input
                            name="isTier"
                            type="radio"
                            value
                            checked={ details.isTier !== null && details.isTier }
                            onChange={ e => this.setState({ details: { ...details, isTier: true } }) }
                            disabled={ disableIntermediaryCheckbox }
                            data-testid="checkbox-Intermediary-bo"
                          />
                          <i className="form-icon" /> Intermediary
                        </label>
                        <label className={ `form-checkbox inline ${ checkboxState.formsDisableOwnerCheckbox ? 'tooltip' : '' }` } data-tooltip={ disabledOwnerToolTipText }>
                          <input
                            name="isTier"
                            type="radio"
                            value={ false }
                            checked={ details.isTier !== null && !details.isTier }
                            onChange={ e => this.setState({ details: { ...details, isTier: false } }) }
                            disabled={ checkboxState.formsDisableOwnerCheckbox }
                            data-testid="checkbox-owner-bo"
                          />
                          <i className="form-icon" /> Owner
                        </label>
                      </div>
                    </React.Fragment>}
                    { uploadedFormType?.description && <React.Fragment>
                      <div className="col-5 italic">Uploaded Form Type</div>
                      <div className="col-7 italic" id={ `bo_${ validationLineNo }_formtype` }>{ uploadedFormType?.description }</div>
                    </React.Fragment> }
                  </div>
                </div>
              </div>
              { validationResultLine && <WhdValidationSummary sectionTitle={ name || 'Owner' } validationResult={ validationResultLine } /> }
            </div>
            <div className="col-3 align-vertical-center">
              <div className="allocation-container">
                <div className="allocation-group-container">
                  <StatementAllocationBlock
                    stmtAllocTitle={ BeneficialOwner.getStmtAllocationTitle() }
                    isEdit={ isEdit }
                    isImyLayer={ isImyLayer }
                    beneficialOwner={ beneficialOwner }
                    allowLayerAllocationChange={ allowLayerAllocationChange }
                    details={ details }
                    onIncomeAllocationChange={ this.onIncomeAllocationChange }
                    displayStatementAllocationValue={ this.displayStatementAllocationValue }
                    disabled={ tierAllocationChanged }
                  />
                  {/* Tier Allocation */}
                  { (!isPooled && !hideTierAllocationValue && BeneficialOwner.shouldDisplayTierAllocationValue(levelParents?.length, isImyLayer))
                    && <TierAllocationBlock
                      tierAllocTitle="Tier Allocation"
                      beneficialOwner={ beneficialOwner }
                      displayTierAllocationValue={ this.displayTierAllocationValue }
                      isEdit={ isEdit }
                      onTierAllocationChange={ this.onTierAllocationChange }
                      isImyLayer={ isImyLayer }
                      details={ details }
                      disabled={ stmtAllocationChanged }
                    />}
                </div>

                {/* Tier Ownership Value (sum of children, only for IMY with recipients) */}
                { (!isPooled && isImyLayer && BeneficialOwner.shouldDisplayTierOwnership(beneficialOwner))
                  && <TierOwnershipBlock
                    intermediaryTitle={ BeneficialOwner.getIntermediaryForText(BeneficialOwner.getIntermediaryForOwnersValue(beneficialOwner)) }
                    ownershipValue={ BeneficialOwner.getTierOwnershipPercent(beneficialOwner, decimalPrecision) }
                    chapter3Status={ beneficialOwner.ownerDetails.chapter3RecipientCode || '' }
                  />}

                {/* Action Buttons */}
                { isEdit && <div className="owner-action-btns-container">
                  <div className="action-btn"><button className="btn btn-primary" type="button" disabled={ isSavingStatement } onClick={ () => this.submitData() } data-testid="btn-save-bo">
                    <i className="icon icon-2x icon-check mr-2" />Save</button>
                  </div>
                  <div className="action-btn">
                    <button className="btn btn-secondary" type="button" disabled={ isSavingStatement } onClick={ () => this.setState({ isEdit: !isEdit }) }><i className="icon icon-2x icon-cross mr-2" />Cancel</button></div>
                  </div> }
                { !isEdit && <div className="owner-action-btns-container">
                    { hasFormUploaded && <div className="action-btn">
                      { AccessControl.hasPermission(permissions, [ActivityPerms.scannedView, ActivityPerms.scannedEdit, ActivityPerms.scannedValidate]) && <FormViewButton
                        permissions={ permissions }
                        formId={ ownerFormId }
                        form={ buildFormObject(beneficialOwnerObject.basicDetails) }
                        content={ <React.Fragment><i className="icon icon-2x icon-apps mr-2" />Form</React.Fragment> }
                        className="btn btn-secondary"
                        title="View Form"
                      /> }
                    </div> }
                    { AccessControl.hasPermission(permissions, [ActivityPerms.whdViewRates]) && isPooled && isStructureValid && <div className="action-btn">
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={ () => { openPooledRatesModal(pooledRecipient.guid, pooledRecipient.rates); } }
                        title="View Withholding Rates"
                        data-testid="btn-view-withholding-rates"
                      ><i className="icon icon-2x icon-calculator" /></button>
                      </div>}
                    { canEdit && <div className="action-btn">
                      <button
                        className="btn btn-secondary"
                        type="button"
                        disabled={ isSavingStatement }
                        onClick={ () => this.setState({ isEdit: !isEdit }) }
                        title="Edit"
                        data-testid="btn-edit"
                      >
                        <i className="icon icon-2x icon-edit mr-2" />Edit
                      </button>
                    </div> }
                    { canEdit && <div className="action-btn">
                      <button
                        className="btn btn-secondary"
                        disabled={ isDeletingRow }
                        type="button"
                        onClick={ () => this.openDeleteConfirmation() }
                        title="Delete"
                        data-testid="btn-delete"
                      >
                        <i className="icon icon-2x icon-delete mr-2" />Delete
                      </button></div> }
                  </div> }
              </div>
            </div>

            { (isDetailed || isSimple) && <div className={ `col-12 section additional-details ${ isAdditionalExpanded ? '' : 'collapsed' }` }>
              <div className="separator" />
              <div className="columns">
                <div className="col-9">
                  <div className="columns">
                    <div className="col-6">
                      <h3>Additional Address Fields</h3>
                      <div className="columns">
                        <div className="col-5 panel-item-name">Street Address</div>
                        { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.address) }</div> }
                        { isEdit && <input type="text" className=" col-5 form-input" onChange={ e => this.setState({ details: { ...details, address: e.target.value } }) } value={ details.address || '' } data-testid="field-street-address-bo" /> }
                        { isDetailed && <React.Fragment>
                          <div className="col-5 panel-item-name">City</div>
                          { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.city) }</div> }
                          { isEdit && <input type="text" className=" col-5 form-input" onChange={ e => this.setState({ details: { ...details, city: e.target.value } }) } value={ details.city || '' } data-testid="field-city-bo" /> }
                          <div className="col-5 panel-item-name">State / Province</div>
                          { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.stateProvince) }</div> }
                          { isEdit
                            && <input
                              type="text"
                              className=" col-5 form-input"
                              onChange={ e => this.setState({ details: { ...details, stateProvince: e.target.value } }) }
                              value={ details.stateProvince || '' }
                              data-testid="field-state-province-bo"
                            /> }
                          <div className="col-5 panel-item-name">ZIP / Postal Code</div>
                          { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.zip) }</div> }
                          { isEdit && <input type="text" className=" col-5 form-input" onChange={ e => this.setState({ details: { ...details, zip: e.target.value } }) } value={ details.zip || '' } data-testid="field-zip-postal-code-bo" /> }
                          <div className="col-5 panel-item-name">Country</div>
                          { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.country) }</div> }
                          { isEdit && <JurisdictionDropDown value={ details.country || '' } onChange={ value => this.setState({ details: { ...details, country: value } }) } data-testid="field-country-bo" /> }
                        </React.Fragment> }
                      </div>
                    </div>
                    { isDetailed && <div className="col-6">
                      <h3>Further Details</h3>
                      <div className="columns">
                        <div className="col-5 panel-item-name">New Beneficial Owner?</div>
                        { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.new) }</div> }
                        { isEdit && <select className="form-select col-5" value={ details.new || '' } onChange={ e => this.setState({ details: { ...details, new: e.target.value } }) } data-testid="dropdown-new-bo-bo">
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select> }
                        <div className="col-5 panel-item-name">Active Beneficial Owner?</div>
                        { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.active) }</div> }
                        { isEdit && <select className="form-select col-5" value={ details.active || '' } onChange={ e => this.setState({ details: { ...details, active: e.target.value } }) } data-testid="dropdown-active-bo-bo">
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select> }
                        <div className="col-5 panel-item-name">U.S. TIN Type</div>
                        { !isEdit && <div className="col-7">{ valueOrEmpty(ownerDetails.tinType) }</div> }
                        { isEdit && <select className="form-select col-5" value={ details.tinType || '' } onChange={ e => this.setState({ details: { ...details, tinType: e.target.value } }) } data-testid="dropdown-us-tin-type-bo">
                          <option value="">Please select</option>
                          <option value="SSN">SSN</option>
                          <option value="EIN">EIN</option>
                        </select> }
                        <div className="col-5 panel-item-name">Tax Form Type</div>
                        { !isEdit && <div className="col-7">{ valueOrEmpty(formType && formType.getFamilyName()) }</div> }
                        { isEdit && <select
                          className="form-select col-5"
                          value={ details.formType || '' }
                          onChange={ e => this.onFormTypeChange(e) }
                          data-testid="dropdown-tax-form-type-bo"
                        >
                          <option value=""> Please select</option>
                          <option value={ 1 }>W-8BEN Jul-2017</option>
                          <option value={ 2 }>W-8BEN-E Apr-2016</option>
                          <option value={ 3 }>W-8IMY Sep-2016</option>
                          <option value={ 4 }>W-9 Dec-2014</option>
                          <option value={ 9 }>W-8EXP Jul-2017</option>
                          <option value={ 10 }>W-8IMY Jun-2017</option>
                          <option value={ 11 }>W-8BEN-E Jul-2017</option>
                          <option value={ 16 }>W-8ECI Feb-2014</option>
                          <option value={ 12 }>W-8ECI Jul-2017</option>
                          <option value={ 17 }>W-8EXP Sep-2016</option>
                          <option value={ 18 }>W-8BEN Feb-2014</option>
                          <option value={ 19 }>W-9 Nov-2017</option>
                          <option value={ 20 }>W-9 Oct-2018</option>
                          <option value={ 36 }>W-9 Mar-2024</option>
                          <option value={ 26 }>W-8BEN Oct-2021</option>
                          <option value={ 27 }>W-8ECI Oct-2021</option>
                          <option value={ 28 }>W-BEN-E Oct-2021</option>
                          <option value={ 29 }>W-8IMY Oct-2021</option>
                          <option value={ 37 }>W-8EXP Oct-2023</option>
                        </select> }
                      </div>
                    </div> }
                  </div>
                </div>
                { isDetailed && <div className="col-3">
                  <div className="columns pad-h3-absence">
                    <div className="col-7 panel-item-name">Treaty Benefits</div>
                    { !isEdit && <div className="col-3">{ valueOrEmpty(ownerDetails.treatyBenefits) }</div> }
                    { isEdit && <select className="form-select col-3" value={ details.treatyBenefits || '' } onChange={ e => this.setState({ details: { ...details, treatyBenefits: e.target.value } }) } data-testid="dropdown-treaty-benefits-bo">
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select> }
                    <div className="col-7 panel-item-name">Limitation on Benefits</div>
                    { !isEdit && <div className="col-5">{ valueOrEmpty(BeneficialOwner.getLOBDescriptionByCode(ownerDetails.limitationOnBenefits)) }</div> }
                    { isEdit
                      && <select
                        className="form-select col-4"
                        value={ details.limitationOnBenefits || '' }
                        onChange={ e => this.setState({ details: { ...details, limitationOnBenefits: e.target.value } }) }
                        data-testid="dropdown-limitation-on-benefits-bo"
                      >
                        <option value="">Please select</option>
                        { LOBCodes.map(x => (<option key={ x.value } value={ x.value }>{ x.description }</option>)) }
                      </select> }
                  </div>
                </div> }
              </div>
            </div> }
            { (isDetailed || isSimple) && <div className="additional-details-toggle-container">
              <button type="button" className="btn additional-details-toggle-button" onClick={ this.toggleExpandedAdditionalDetails } title={ `${ isAdditionalExpanded ? 'Hide' : 'Show' } Additional Details` }>
                <span className={ `icon icon-arrow-${ isAdditionalExpanded ? 'up' : 'down' }` } />
              </button>
            </div>}
          </div>
        </div> }
      </div>
      { beneficialOwner.recipients && <div className="tier-owner-details">
        { beneficialOwner.recipients.map((x, childIdx) => <BeneficialOwner
          key={ x.guid || (x.ownerDetails && x.ownerDetails.guid) }
          id={ BeneficialOwner.buildElementId(x.name || x.ownerDetails?.name || x.basicDetails?.name) }
          beneficialOwner={ x }
          selectedPills={ selectedPills }
          decimalPrecision={ decimalPrecision }
          getChapter3DescriptionByName={ getChapter3DescriptionByName }
          getChapter4DescriptionByName={ getChapter4DescriptionByName }
          idx={ childIdx }
          openChangeParentModal={ openChangeParentModal }
          parents={ levelParents }
          statementType={ statementType }
          isTopTierExpanded={ isTopTierExpanded }
          isParentTierExpanded={ !isTierCollapsed }
          dispatch={ dispatch }
          permissions={ permissions }
          openAccountWithholdingModal={ formId => openAccountWithholdingModal(formId) }
          topAccountId={ topAccountId }
          parentAccountId={ ownerAccountId }
          openAddFormModal={ openAddFormModal }
          addBeneficialOwner={ addBeneficialOwner }
          isSavingStatement={ isSavingStatement }
          updateToggledCollapsibleTiers={ (accountName, toggleState) => updateToggledCollapsibleTiers(accountName, toggleState) }
          tierCollapseStructure={ tierCollapseStructure }
          validationLineNo={ x.ownerDetails?.lineNo || null }
          validationResult={ validationResult }
          openPooledRatesModal={ (accountName, rates) => openPooledRatesModal(accountName, rates) }
          isDeletingRow={ isDeletingRow }
          isStructureValid={ isStructureValid }
          isAddingNewBeneficalOwner={ isAddingNewBeneficalOwner }
          statementVersion={ statementVersion }
        />) }
      </div> }
      <ConfirmModal
        key="deleteRowConfirmation"
        isShown={ rowToDelete != null }
        isPerformingAction={ isDeletingRow }
        onConfirm={ () => this.deleteRow() }
        onCancel={ () => {
          this.closeDeleteConfirmation();
        } }
        confirmTitle="Continue"
        cancelTitle="Close"
        title="Are you sure?"
        message={ beneficialOwner?.recipients
          ? 'Deleting an intermediary will also delete all of the beneficial owners beneath it'
          : 'Deleting a statement row can not be undone' }
      />
      <ConfirmModal
        key="updateRowConfirmation"
        isShown={ showUpdateConfirmation === true }
        isPerformingAction={ isSavingStatement }
        onConfirm={ () => this.confirmUpdate() }
        onCancel={ () => {
          this.cancelUpdate();
        } }
        confirmTitle="Confirm"
        cancelTitle="Close"
        title="Change Intermediary to Owner"
        message={ <React.Fragment>
          <span>Are you sure you want to change this Intermediary to an Owner?</span>
          <ul>
            <li>This action will remove { rowToUpdate?.owners ?? 'all' } underlying Beneficial Owners from the Withholding Statement.</li>
            <li>Beneficial Owner Forms will not be affected but their Accounts will be made inactive.</li>
          </ul>
        </React.Fragment> }
      />
    </React.Fragment>;
  }
}

export default BeneficialOwner;
